import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Import Shared Components
import { Modal, Loader, IconButton } from 'shared-components';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
// Import Global Components
import Heading from '../../../../components/Heading';
// TODO: add or remove set pin functionality based on whether or not we allow it for Galileo programs
// import Button from '../../../../components/Button';
import Text from '../../../../components/Text';
import Alert from '../../../../components/Alert';
// Import Translations
import { profileEn } from '../../../../i18n/profile';
// Import Local Components
import PasswordConfirmModal, {
  isStepUpRequiredError,
  reverifyPassword,
} from '../PasswordConfirmModal';
// Import Local Layouts
import PinModal from '../../layouts/PinModal';
// Import Component Styles
import { PinContainer, PinFrame } from './styles';

// TODO: add or remove set pin functionality based on whether or not we allow it for Galileo programs
const GetPinModal = ({
  toggleModal,
  // toggleSetPinModal,
  pin,
  setPin,
  currentAccount,
  alert,
  setAlert,
  initialPinFetch,
  allowSensitiveDataToken,
}) => {
  const [loading, setLoading] = useState(false);
  // iframe-enabled programs render the PIN in a hosted iframe instead
  const [iframeUrl, setIframeUrl] = useState(null);
  // step-up re-password prompt for the sensitive-data mint
  const [needsPasswordConfirm, setNeedsPasswordConfirm] = useState(false);
  const [passwordConfirmError, setPasswordConfirmError] = useState(null);
  // TODO: add or remove set pin functionality based on whether or not we allow it for Galileo programs
  // const isError = !!alert && alert.type === 'danger';

  const fetchData = async (isRetry = false) => {
    setLoading(true);
    try {
      const params = {
        last_four_digits: currentAccount.cards[0].last_four_digits,
      };

      // Mint first; iframe-enabled programs return a hosted iframe URL and
      // the PIN never reaches the browser. The mint is only authorized for
      // programs with allow_sensitive_data_token, so others skip straight to
      // the legacy PIN fetch.
      if (allowSensitiveDataToken) {
        const mintRes = await post(
          `/api/v1/cardholder_portal/cardholder/accounts/${currentAccount.id}/sensitive_data`,
          { ...params, form_type: 'pin' }
        );

        if (mintRes.data.iframe_url) {
          setIframeUrl(mintRes.data.iframe_url);
          setLoading(false);
          return;
        }
      }

      const res = await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${currentAccount.id}/get_pin`,
        params
      );

      setPin(res.data.pin);
    } catch (e) {
      // Step-up: prompt for the password, then retry the mint once
      if (!isRetry && isStepUpRequiredError(e)) {
        setPasswordConfirmError(null);
        setNeedsPasswordConfirm(true);
      } else {
        setAlert({ type: 'danger', message: e.message });
      }
    }
    setLoading(false);
  };

  /* eslint-disable no-param-reassign */
  useEffect(() => {
    // One-shot per open: the parent re-arms initialPinFetch when the modal
    // opens, and error remounts must not refetch (the alert remounts this
    // modal, so refetching here would loop on a failing endpoint)
    if (!initialPinFetch.current) {
      return;
    }
    initialPinFetch.current = false;
    fetchData();
  }, [
    pin,
    currentAccount.cards,
    currentAccount.id,
    setPin,
    setAlert,
    initialPinFetch,
  ]);
  /* eslint-enable no-param-reassign */

  const page = 'profile';

  const handlePasswordConfirm = async password => {
    try {
      await reverifyPassword(password);
    } catch {
      setPasswordConfirmError(profileEn[`${page}-confirm-password-error`]);
      return;
    }
    setNeedsPasswordConfirm(false);
    setPasswordConfirmError(null);
    await fetchData(true);
  };

  if (needsPasswordConfirm) {
    return (
      <PasswordConfirmModal
        onConfirm={handlePasswordConfirm}
        onCancel={toggleModal}
        error={passwordConfirmError}
      />
    );
  }

  return (
    <Modal>
      <PinModal>
        <IconButton
          iconType="x"
          onClick={toggleModal}
          className="close-modal"
        />
        {/* alert for both error and success */}
        {alert && (
          <Alert
            alert={alert.message}
            alertFor={alert.type}
            page={page}
            type={alert.type}
          />
        )}
        <Heading
          page={`${page}-get-pin`}
          heading={profileEn[`${page}-get-pin-heading`]}
        />
        {loading ? (
          <>
            <Text
              text={profileEn[`${page}-get-pin-wait-text`]}
              textFor="get-pin-wait"
              page={page}
            />
            <Loader />
          </>
        ) : (
          <PinContainer>
            {iframeUrl ? (
              <PinFrame
                src={iframeUrl}
                title={profileEn[`${page}-get-pin-heading`]}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <Text
                text={profileEn[`${page}-get-pin-text`]}
                textFor="get-pin"
                page={page}
                values={{
                  b: chunks => <b>{chunks}</b>,
                  pin,
                }}
              />
            )}
            {/* TODO: add or remove set pin functionality based on whether or not we allow it for Galileo programs */}
            {/* Don't allow users to access setPinModal if there is error fetching pin
            {!isError && (
              <Button
                buttonText={profileEn[`${page}-set-pin-button`]}
                buttonFor="set-pin"
                page={page}
                type="secondary"
                onClick={e => {
                  e.preventDefault();
                  toggleSetPinModal();
                }}
              />
            )} */}
          </PinContainer>
        )}
      </PinModal>
    </Modal>
  );
};

GetPinModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  // toggleSetPinModal: PropTypes.func.isRequired,
  pin: PropTypes.string,
  setPin: PropTypes.func.isRequired,
  currentAccount: PropTypes.shape({
    id: PropTypes.number.isRequired,
    cards: PropTypes.array.isRequired, //eslint-disable-line
  }).isRequired,
  alert: PropTypes.shape({
    message: PropTypes.string.isRequired,
    messageFor: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  setAlert: PropTypes.func.isRequired,
  initialPinFetch: PropTypes.shape({
    current: PropTypes.bool.isRequired,
  }).isRequired,
  allowSensitiveDataToken: PropTypes.bool,
};

GetPinModal.defaultProps = {
  pin: null,
  allowSensitiveDataToken: false,
};
export default GetPinModal;
